import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from '../components/Navbar';

const fmtDate = (ts) => ts?.toDate?.()?.toLocaleDateString() ?? '—';
const fmtDateTime = (ts) =>
  ts?.toDate?.()?.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) ?? '—';
const fmtTime = (ts) => ts?.toDate?.()?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) ?? '—';

const breakHours = (e) =>
  (e.breaks || []).reduce(
    (sum, b) => (b.breakIn && b.breakOut ? sum + (b.breakOut.seconds - b.breakIn.seconds) / 3600 : sum),
    0
  );

// Net hours for a closed entry (clock-in to clock-out, minus completed breaks)
const entryHours = (e) => {
  if (!e.clockIn || !e.clockOut) return 0;
  const gross = (e.clockOut.seconds - e.clockIn.seconds) / 3600;
  return Math.max(gross - breakHours(e), 0);
};

function StatusBadge({ status }) {
  const map = {
    'clocked-in': { label: 'Clocked in', color: '#4ade80' },
    'on-break': { label: 'On break', color: '#fbbf24' },
    off: { label: 'Off the clock', color: '#666' },
  };
  const m = map[status] || map.off;
  return (
    <span style={{ ...s.badge, backgroundColor: m.color + '22', color: m.color, border: `1px solid ${m.color}44` }}>
      {m.label}
    </span>
  );
}

function EntryStatusBadge({ entry }) {
  const late = entry.status === 'late';
  const noShift = entry.status === 'no-shift';
  const color = late ? '#f87171' : noShift ? '#999' : '#4ade80';
  const label = (late ? 'Late' : noShift ? 'No shift' : 'On time') + (entry.pardoned ? ' (pardoned)' : '');
  return (
    <span style={{ ...s.badge, backgroundColor: color + '22', color, border: `1px solid ${color}44` }}>
      {label}
    </span>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [cid, setCid] = useState(null);
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // Resolve the effective companyId — legacy admins can have companyId != uid,
  // with the company doc keyed either way.
  useEffect(() => {
    (async () => {
      try {
        const adminSnap = await getDoc(doc(db, 'users', id));
        const admin = adminSnap.exists() ? adminSnap.data() : null;
        const effective = admin?.companyId || id;
        let coSnap = await getDoc(doc(db, 'companies', id));
        if (!coSnap.exists() && effective !== id) coSnap = await getDoc(doc(db, 'companies', effective));
        const co = coSnap.exists() ? coSnap.data() : {};
        setCompany({
          companyName: co.companyName || '(No company name)',
          adminName: co.adminName || admin?.name || '',
          adminEmail: co.adminEmail || admin?.email || '',
          createdAt: co.createdAt || admin?.createdAt || null,
          locked: co.locked || false,
          paid: co.paid || false,
        });
        setCid(effective);
      } catch (e) {
        alert('Error loading company: ' + e.message);
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!cid) return;
    const unsubs = [
      onSnapshot(query(collection(db, 'users'), where('companyId', '==', cid)), (snap) => {
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }),
      onSnapshot(query(collection(db, 'timeEntries'), where('companyId', '==', cid)), (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),
      onSnapshot(query(collection(db, 'timeOffRequests'), where('companyId', '==', cid)), (snap) => {
        setTimeOff(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, [cid]);

  const entriesByUser = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (!e.userId) return;
      (map[e.userId] = map[e.userId] || []).push(e);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (b.clockIn?.seconds || 0) - (a.clockIn?.seconds || 0)));
    return map;
  }, [entries]);

  const statsFor = (empId) => {
    const list = entriesByUser[empId] || [];
    const open = list.find((e) => e.clockIn && !e.clockOut);
    const cutoff = Date.now() / 1000 - 30 * 86400;
    const last30 = list.filter((e) => (e.clockIn?.seconds || 0) >= cutoff);
    const closed30 = last30.filter((e) => e.clockOut);
    const allClosed = list.filter((e) => e.clockOut);
    const requests = timeOff.filter((r) => r.userId === empId);
    return {
      status: open ? (open.onBreak ? 'on-break' : 'clocked-in') : 'off',
      openEntry: open,
      lastEntry: list[0] || null,
      shifts30: last30.length,
      hours30: closed30.reduce((sum, e) => sum + entryHours(e), 0),
      lates30: last30.filter((e) => e.status === 'late').length,
      overtime30: last30.reduce((sum, e) => sum + (e.overtime ? e.overtimeHours || 0 : 0), 0),
      shiftsAll: list.length,
      hoursAll: allClosed.reduce((sum, e) => sum + entryHours(e), 0),
      pendingTimeOff: requests.filter((r) => r.status === 'pending').length,
      approvedDays: requests.filter((r) => r.status === 'approved').reduce((sum, r) => sum + (r.days || 0), 0),
      recentEntries: list.slice(0, 8),
    };
  };

  const employees = users
    .filter((u) => u.role === 'employee')
    .filter((u) => {
      const q = search.toLowerCase();
      return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const allEmployees = users.filter((u) => u.role === 'employee');
  const clockedInCount = allEmployees.filter((u) => statsFor(u.id).status !== 'off').length;
  const teamHours30 = allEmployees.reduce((sum, u) => sum + statsFor(u.id).hours30, 0);
  const pendingTimeOffCount = timeOff.filter((r) => r.status === 'pending').length;

  const selected = selectedId ? users.find((u) => u.id === selectedId) : null;
  const selStats = selected ? statsFor(selected.id) : null;

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.container}>
        <button style={s.backBtn} onClick={() => navigate('/')}>&larr; All companies</button>

        <div style={s.headerRow}>
          <div>
            <h1 style={s.title}>{company?.companyName || 'Loading...'}</h1>
            {company && (
              <div style={s.subTitle}>
                Admin: {company.adminName || '—'} <span style={{ color: '#555' }}>({company.adminEmail || '—'})</span>
                {' · '}Joined {fmtDate(company.createdAt)}
              </div>
            )}
          </div>
          {company && (
            <div style={{ display: 'flex', gap: 8 }}>
              {company.paid && (
                <span style={{ ...s.badge, backgroundColor: '#4f46e522', color: '#818cf8', border: '1px solid #4f46e544' }}>Paid</span>
              )}
              {company.locked && (
                <span style={{ ...s.badge, backgroundColor: '#f8717122', color: '#f87171', border: '1px solid #f8717144' }}>Locked</span>
              )}
            </div>
          )}
        </div>

        <div style={s.statsRow}>
          <div style={s.statBox}>
            <div style={s.statNum}>{allEmployees.length}</div>
            <div style={s.statLabel}>Employees</div>
          </div>
          <div style={s.statBox}>
            <div style={{ ...s.statNum, color: '#4ade80' }}>{clockedInCount}</div>
            <div style={s.statLabel}>Clocked In Now</div>
          </div>
          <div style={s.statBox}>
            <div style={{ ...s.statNum, color: '#818cf8' }}>{teamHours30.toFixed(1)}</div>
            <div style={s.statLabel}>Team Hours (30d)</div>
          </div>
          <div style={s.statBox}>
            <div style={{ ...s.statNum, color: '#fb923c' }}>{pendingTimeOffCount}</div>
            <div style={s.statLabel}>Pending Time Off</div>
          </div>
        </div>

        <input
          style={s.search}
          placeholder="Search employees by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : employees.length === 0 ? (
          <div style={s.empty}>No employees found.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {['Employee', 'Status', 'Hours (30d)', 'Shifts (30d)', 'Lates (30d)', 'Last Active'].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const st = statsFor(emp.id);
                return (
                  <tr key={emp.id} className="row-clickable" style={s.tr} onClick={() => setSelectedId(emp.id)}>
                    <td style={s.td}>
                      <div style={s.empName}>{emp.name || '(No name)'}</div>
                      <div style={s.empEmail}>{emp.email}</div>
                    </td>
                    <td style={s.td}><StatusBadge status={st.status} /></td>
                    <td style={s.td}>{st.hours30.toFixed(1)}</td>
                    <td style={s.td}>{st.shifts30}</td>
                    <td style={{ ...s.td, color: st.lates30 > 0 ? '#f87171' : '#fff' }}>{st.lates30}</td>
                    <td style={{ ...s.td, color: '#999', fontSize: 13 }}>
                      {st.status !== 'off'
                        ? `In since ${fmtTime(st.openEntry?.clockIn)}`
                        : st.lastEntry
                        ? fmtDateTime(st.lastEntry.clockOut || st.lastEntry.clockIn)
                        : 'Never clocked in'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && selStats && (
        <div style={s.overlay} onClick={() => setSelectedId(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h2 style={s.modalTitle}>{selected.name || '(No name)'}</h2>
                  <StatusBadge status={selStats.status} />
                </div>
                <div style={s.empEmail}>{selected.email}</div>
              </div>
              <button style={s.closeBtn} onClick={() => setSelectedId(null)}>Close</button>
            </div>

            <div style={s.factGrid}>
              <div style={s.fact}>
                <div style={s.factLabel}>Joined</div>
                <div style={s.factValue}>{fmtDate(selected.createdAt)}</div>
              </div>
              <div style={s.fact}>
                <div style={s.factLabel}>Flexible Clock-In</div>
                <div style={s.factValue}>{selected.flexibleClockIn ? 'Yes' : 'No'}</div>
              </div>
              <div style={s.fact}>
                <div style={s.factLabel}>
                  {selStats.status !== 'off' ? 'Clocked In Since' : 'Last Clock-Out'}
                </div>
                <div style={s.factValue}>
                  {selStats.status !== 'off'
                    ? fmtDateTime(selStats.openEntry?.clockIn)
                    : fmtDateTime(selStats.lastEntry?.clockOut || selStats.lastEntry?.clockIn)}
                </div>
              </div>
              <div style={s.fact}>
                <div style={s.factLabel}>User ID</div>
                <div style={{ ...s.factValue, fontSize: 11, fontFamily: 'monospace', color: '#999', wordBreak: 'break-all' }}>{selected.id}</div>
              </div>
            </div>

            <div style={s.sectionTitle}>Last 30 Days</div>
            <div style={s.miniStatsRow}>
              <div style={s.miniStat}><div style={s.miniNum}>{selStats.shifts30}</div><div style={s.miniLabel}>Shifts</div></div>
              <div style={s.miniStat}><div style={s.miniNum}>{selStats.hours30.toFixed(1)}</div><div style={s.miniLabel}>Hours</div></div>
              <div style={s.miniStat}><div style={{ ...s.miniNum, color: selStats.lates30 > 0 ? '#f87171' : '#4ade80' }}>{selStats.lates30}</div><div style={s.miniLabel}>Late Arrivals</div></div>
              <div style={s.miniStat}><div style={s.miniNum}>{selStats.overtime30.toFixed(1)}</div><div style={s.miniLabel}>Overtime Hrs</div></div>
            </div>

            <div style={s.sectionTitle}>All Time</div>
            <div style={s.miniStatsRow}>
              <div style={s.miniStat}><div style={s.miniNum}>{selStats.shiftsAll}</div><div style={s.miniLabel}>Total Shifts</div></div>
              <div style={s.miniStat}><div style={s.miniNum}>{selStats.hoursAll.toFixed(1)}</div><div style={s.miniLabel}>Total Hours</div></div>
              <div style={s.miniStat}><div style={{ ...s.miniNum, color: '#fb923c' }}>{selStats.pendingTimeOff}</div><div style={s.miniLabel}>Pending Time Off</div></div>
              <div style={s.miniStat}><div style={s.miniNum}>{selStats.approvedDays}</div><div style={s.miniLabel}>Time Off Days Taken</div></div>
            </div>

            <div style={s.sectionTitle}>Recent Time Entries</div>
            {selStats.recentEntries.length === 0 ? (
              <div style={{ ...s.empty, padding: 24 }}>No time entries yet.</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Date', 'In', 'Out', 'Break', 'Hours', 'Status'].map((h) => (
                      <th key={h} style={{ ...s.th, padding: '10px 14px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selStats.recentEntries.map((e) => (
                    <tr key={e.id} style={s.tr}>
                      <td style={s.tdSm}>{e.date || fmtDate(e.clockIn)}</td>
                      <td style={s.tdSm}>{fmtTime(e.clockIn)}</td>
                      <td style={s.tdSm}>{e.clockOut ? fmtTime(e.clockOut) : <span style={{ color: '#4ade80' }}>Active</span>}</td>
                      <td style={s.tdSm}>{breakHours(e) > 0 ? breakHours(e).toFixed(2) + 'h' : '—'}</td>
                      <td style={s.tdSm}>{e.clockOut ? entryHours(e).toFixed(2) : '—'}</td>
                      <td style={s.tdSm}><EntryStatusBadge entry={e} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', backgroundColor: '#0f0f0f' },
  container: { padding: '32px 40px', maxWidth: 1200, margin: '0 auto' },
  backBtn: { backgroundColor: 'transparent', color: '#999', border: '1px solid #333', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, marginBottom: 20 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 6 },
  subTitle: { color: '#999', fontSize: 14 },
  statsRow: { display: 'flex', gap: 16, marginBottom: 24 },
  statBox: { flex: 1, backgroundColor: '#1e1e1e', borderRadius: 12, padding: 20, textAlign: 'center' },
  statNum: { fontSize: 32, fontWeight: 'bold', color: '#4f46e5', marginBottom: 4 },
  statLabel: { fontSize: 13, color: '#999' },
  search: { width: '100%', backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: 10, padding: '10px 16px', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 20 },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#1e1e1e', borderRadius: 12, overflow: 'hidden' },
  th: { padding: '14px 20px', textAlign: 'left', color: '#999', fontSize: 12, fontWeight: '600', borderBottom: '1px solid #2a2a2a', textTransform: 'uppercase', letterSpacing: '0.5px' },
  tr: { borderBottom: '1px solid #2a2a2a' },
  td: { padding: '14px 20px', color: '#fff', fontSize: 14, verticalAlign: 'middle' },
  tdSm: { padding: '10px 14px', color: '#fff', fontSize: 13, verticalAlign: 'middle' },
  empName: { fontWeight: 'bold', fontSize: 15 },
  empEmail: { color: '#666', fontSize: 12, marginTop: 2 },
  badge: { padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 'bold', whiteSpace: 'nowrap' },
  empty: { color: '#999', textAlign: 'center', padding: 60, fontSize: 15 },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '48px 16px', zIndex: 200, overflowY: 'auto' },
  modal: { backgroundColor: '#161616', border: '1px solid #2a2a2a', borderRadius: 16, padding: 28, width: '100%', maxWidth: 760 },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  modalTitle: { color: '#fff', fontSize: 22, fontWeight: 'bold' },
  closeBtn: { backgroundColor: 'transparent', color: '#999', border: '1px solid #333', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  factGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 8 },
  fact: { backgroundColor: '#1e1e1e', borderRadius: 10, padding: '12px 16px' },
  factLabel: { color: '#999', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  factValue: { color: '#fff', fontSize: 14, fontWeight: '600' },
  sectionTitle: { color: '#999', fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '20px 0 10px' },
  miniStatsRow: { display: 'flex', gap: 12 },
  miniStat: { flex: 1, backgroundColor: '#1e1e1e', borderRadius: 10, padding: '14px 12px', textAlign: 'center' },
  miniNum: { fontSize: 22, fontWeight: 'bold', color: '#818cf8', marginBottom: 2 },
  miniLabel: { fontSize: 11, color: '#999' },
};
